import ExcelJS from "exceljs";
import { formatAmount, formatDateWithYear } from "../hooks/dataHandler";
import { buildCalculatorNotes } from "../pdf/page";

export class BaseExcelGenerator {
  constructor(settings = {}) {
    this.workbook = new ExcelJS.Workbook();
    this.currentSheet = null;
    this.currentRow = 1;
    this._usedSheetNames = new Set();
    this._activeScheduleKey = null;
    this.settings = {
      includeNotes: true,
      ...settings,
      styles: this.createDefaultStyles(),
    };
  }

  resetCurrentSheet() {
    this.currentSheet = null;
    this.currentRow = 1;
    this._activeScheduleKey = null;
  }

  buildScheduleKey(company, schedule) {
    const companyKey =
      company?.tc_id || company?.c_id || company?.id || company?.uen || "";
    const scheduleKey =
      schedule?.name || schedule?.type || schedule?.scheduleType || "Schedule";
    const scheduleNo = schedule?.scheduleNo || schedule?.schedule_no || "";
    return `${companyKey}::${scheduleKey}::${scheduleNo}`;
  }

  sanitizeWorksheetName(name) {
    const raw = (name || "Schedule").toString().trim() || "Schedule";
    // Excel limitations: <= 31 chars and cannot contain: \ / ? * [ ]
    const cleaned = raw.replace(/[\\\/\?\*\[\]\:]/g, " ").replace(/\s+/g, " ");
    const truncated = cleaned.slice(0, 31).trim();
    return truncated || "Schedule";
  }

  ensureUniqueWorksheetName(name) {
    const base = this.sanitizeWorksheetName(name);
    if (!this._usedSheetNames.has(base) && !this.workbook.getWorksheet(base)) {
      this._usedSheetNames.add(base);
      return base;
    }

    // Reserve space for suffix like " (2)"
    for (let i = 2; i < 1000; i++) {
      const suffix = ` (${i})`;
      const maxBaseLen = Math.max(1, 31 - suffix.length);
      const candidateBase = base.slice(0, maxBaseLen).trim() || "Schedule";
      const candidate = `${candidateBase}${suffix}`;
      if (
        !this._usedSheetNames.has(candidate) &&
        !this.workbook.getWorksheet(candidate)
      ) {
        this._usedSheetNames.add(candidate);
        return candidate;
      }
    }

    // Fallback
    const fallback = `${base.slice(0, 25)}_${Date.now()}`.slice(0, 31);
    this._usedSheetNames.add(fallback);
    return fallback;
  }

  /** Centralized style definitions */
  createDefaultStyles() {
    return {
      header: {
        font: {
          bold: true,
          size: 11,
          color: { argb: "000000" },
          name: "Calibri",
        },
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "D9D9D9" },
        },
        alignment: { horizontal: "center", vertical: "middle" },
      },
      data: {
        font: { size: 10, color: { argb: "000000" }, name: "Calibri" },
        alignment: { horizontal: "left", vertical: "middle" },
      },
      number: {
        font: { size: 10, color: { argb: "000000" }, name: "Calibri" },
        alignment: { horizontal: "right", vertical: "middle" },
        numFmt: "#,##0",
      },
      currency: {
        font: { size: 10, color: { argb: "000000" }, name: "Calibri" },
        alignment: { horizontal: "right", vertical: "middle" },
        numFmt: '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)',
      },
      total: {
        font: {
          bold: true,
          size: 10,
          color: { argb: "000000" },
          name: "Calibri",
        },
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "F2F2F2" },
        },
        alignment: { horizontal: "right", vertical: "middle" },
        numFmt: '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)',
      },
      bold: {
        font: {
          bold: true,
          size: 10,
          color: { argb: "000000" },
          name: "Calibri",
        },
        alignment: { horizontal: "left", vertical: "middle" },
      },
      redText: {
        font: {
          size: 10,
          color: { argb: "FF0000" },
          name: "Calibri",
        },
        alignment: { horizontal: "center", vertical: "middle" },
      },
      companyHeader: {
        font: {
          bold: true,
          size: 14,
          color: { argb: "000000" },
          name: "Calibri",
        },
        alignment: { horizontal: "left", vertical: "middle" },
      },
      companySubHeader: {
        font: {
          size: 10,
          color: { argb: "000000" },
          name: "Calibri",
        },
        alignment: { horizontal: "left", vertical: "middle" },
      },
      scheduleTitle: {
        font: {
          bold: true,
          size: 12,
          color: { argb: "000000" },
          name: "Calibri",
        },
        alignment: { horizontal: "left", vertical: "middle" },
      },
      notes: {
        font: {
          size: 10,
          color: { argb: "000000" },
          name: "Calibri",
        },
        alignment: { horizontal: "left", vertical: "top", wrapText: true },
      },
      reviewNotes: {
        font: {
          size: 10,
          color: { argb: "FF0000" },
          name: "Calibri",
        },
        alignment: { horizontal: "left", vertical: "top", wrapText: true },
      },
      notesHeader: {
        font: {
          bold: true,
          size: 10,
          color: { argb: "000000" },
          name: "Calibri",
        },
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "E0E0E0" },
        },
        alignment: { horizontal: "left", vertical: "middle" },
        border: {
          bottom: { style: "thin", color: { argb: "000000" } },
        },
      },
      reviewNotesHeader: {
        font: {
          bold: true,
          size: 10,
          color: { argb: "FF0000" },
          name: "Calibri",
        },
        fill: {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFE0E0" },
        },
        alignment: { horizontal: "left", vertical: "middle" },
        border: {
          bottom: { style: "thin", color: { argb: "FF0000" } },
        },
      },
    };
  }

  /**
   * Initialize a new worksheet and add company header
   * Similar to BasePdfGenerator.initializeHeader
   */
  initializeHeader(company, schedule, sheetName = "Schedule") {
    const finalSheetName = this.ensureUniqueWorksheetName(sheetName);
    this.currentSheet = this.workbook.addWorksheet(finalSheetName);
    this.currentRow = 1;
    this._activeScheduleKey = this.buildScheduleKey(company, schedule);

    // Company name (bold, larger)
    const companyNameRow = this.currentSheet.getRow(this.currentRow);
    companyNameRow.getCell(1).value = company.name;
    companyNameRow.getCell(1).style = this.settings.styles.companyHeader;
    companyNameRow.height = 20;
    this.currentRow++;

    // Tax Reference Number
    const uenRow = this.currentSheet.getRow(this.currentRow);
    uenRow.getCell(1).value = `Tax Reference Number: ${company.uen}`;
    uenRow.getCell(1).style = this.settings.styles.companySubHeader;
    this.currentRow++;

    // Tax Computation
    const taxCompRow = this.currentSheet.getRow(this.currentRow);
    taxCompRow.getCell(1).value = "Tax Computation";
    taxCompRow.getCell(1).style = this.settings.styles.companySubHeader;
    this.currentRow++;

    // Year of Assessment
    const yaRow = this.currentSheet.getRow(this.currentRow);
    yaRow.getCell(1).value = `Year of Assessment: ${company.yearOfAssessment}`;
    yaRow.getCell(1).style = this.settings.styles.companySubHeader;
    this.currentRow++;

    // Basis Period
    const basisPeriodRow = this.currentSheet.getRow(this.currentRow);
    basisPeriodRow.getCell(1).value = `Basis Period: ${formatDateWithYear(
      company.financialYearStart
    )} to ${formatDateWithYear(company.financialYearEnd)}`;
    basisPeriodRow.getCell(1).style = this.settings.styles.companySubHeader;
    this.currentRow++;

    // Empty row
    this.currentRow++;

    // Schedule name and schedule number
    const scheduleRow = this.currentSheet.getRow(this.currentRow);
    scheduleRow.getCell(1).value = schedule.name;
    scheduleRow.getCell(1).style = this.settings.styles.scheduleTitle;

    if (schedule.scheduleNo) {
      scheduleRow.getCell(2).value = `Schedule [${schedule.scheduleNo}]`;
      scheduleRow.getCell(2).style = this.settings.styles.scheduleTitle;
    }

    scheduleRow.height = 20;
    this.currentRow++;

    // Empty row before content
    this.currentRow++;
  }

  /**
   * Extract text content from PDF-style cell objects
   * Handles objects like { content: "text", styles: {...} }
   */
  extractCellContent(cell) {
    if (typeof cell === "string" || typeof cell === "number") {
      return cell;
    }
    if (cell && typeof cell === "object" && cell.content !== undefined) {
      return cell.content;
    }
    return cell || "";
  }

  /**
   * Apply cell styling based on PDF-style cell objects
   */
  applyCellStyle(cell, excelCell, columnIndex, columnStyles = {}) {
    const cellContent = this.extractCellContent(cell);

    // Check if cell is a PDF-style object with styles
    if (cell && typeof cell === "object" && cell.styles) {
      const styles = cell.styles;

      // Apply bold if specified
      if (styles.fontStyle === "bold") {
        excelCell.font = { ...excelCell.font, bold: true };
      }

      // Apply italic if specified
      if (styles.fontStyle === "italic") {
        excelCell.font = { ...excelCell.font, italic: true };
      }

      // Apply text color (PDF uses [r, g, b], Excel uses ARGB)
      if (styles.textColor && Array.isArray(styles.textColor)) {
        const [r, g, b] = styles.textColor;
        const argb = `FF${r.toString(16).padStart(2, "0")}${g
          .toString(16)
          .padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
        // Create a new font object to avoid mutating shared style
        excelCell.font = {
          ...(excelCell.font || {}),
          color: { argb },
          bold: excelCell.font?.bold || false,
          italic: excelCell.font?.italic || false,
          size: excelCell.font?.size || 10,
          name: excelCell.font?.name || "Calibri",
        };
      }

      // Apply horizontal alignment
      if (styles.halign) {
        excelCell.alignment = {
          ...excelCell.alignment,
          horizontal: styles.halign,
        };
      }
    }

    // Apply column-specific styles
    if (columnStyles[columnIndex]) {
      const colStyle = columnStyles[columnIndex];
      if (colStyle.halign) {
        excelCell.alignment = {
          ...excelCell.alignment,
          horizontal: colStyle.halign,
        };
      }
    }

    // Default alignment for number columns (right-align)
    if (typeof cellContent === "number" || !isNaN(parseFloat(cellContent))) {
      excelCell.alignment = {
        ...excelCell.alignment,
        horizontal: "right",
      };
      // Try to format as currency if it looks like a number
      if (cellContent !== "" && cellContent !== null) {
        const numValue = parseFloat(cellContent);
        if (!isNaN(numValue)) {
          excelCell.numFmt =
            '_("$"* #,##0.00_);_("$"* (#,##0.00);_("$"* "-"??_);_(@_)';
        }
      }
    }
  }

  /**
   * Build a table similar to PDF's buildAutoTable
   */
  buildAutoTable({
    head,
    body,
    columnStyles = {},
    didParseCell,
    schedule,
    company,
    lineSettings,
  }) {
    const nextKey = this.buildScheduleKey(company, schedule);
    if (!this.currentSheet || this._activeScheduleKey !== nextKey) {
      this.initializeHeader(company, schedule, schedule?.name || "Schedule");
    }

    let startRow = this.currentRow;

    // Add header row
    if (head && head.length > 0) {
      const headerRow = this.currentSheet.getRow(this.currentRow);
      let actualColIndex = 0;

      head[0].forEach((header, colIndex) => {
        const cell = headerRow.getCell(actualColIndex + 1);
        const headerContent = this.extractCellContent(header);
        cell.value = headerContent;

        // Apply base header style properties individually (to avoid shared reference issues)
        const baseHeaderStyle = this.settings.styles.header;
        cell.font = {
          bold: baseHeaderStyle.font.bold,
          size: baseHeaderStyle.font.size,
          color: baseHeaderStyle.font.color,
          name: baseHeaderStyle.font.name,
        };
        cell.fill = {
          type: baseHeaderStyle.fill.type,
          pattern: baseHeaderStyle.fill.pattern,
          fgColor: baseHeaderStyle.fill.fgColor,
        };
        cell.alignment = {
          horizontal: baseHeaderStyle.alignment.horizontal,
          vertical: baseHeaderStyle.alignment.vertical,
        };

        // Check for colSpan
        let colSpan = 1;
        if (header && typeof header === "object" && header.colSpan) {
          colSpan = header.colSpan;
          if (colSpan > 1) {
            this.currentSheet.mergeCells(
              this.currentRow,
              actualColIndex + 1,
              this.currentRow,
              actualColIndex + colSpan
            );
          }
        }

        // Apply custom header styles if provided
        if (didParseCell) {
          const mockData = {
            row: { index: 0, section: "head" },
            column: { index: colIndex },
            cell: { styles: {} },
          };
          didParseCell(mockData);
          if (mockData.cell.styles.halign) {
            cell.alignment = {
              ...cell.alignment,
              horizontal: mockData.cell.styles.halign,
            };
          }
        }

        // Apply cell-specific styles from header object
        this.applyCellStyle(header, cell, colIndex, columnStyles);

        actualColIndex += colSpan;
      });
      headerRow.height = 20;
      this.currentRow++;
    }

    // Add body rows
    if (body && body.length > 0) {
      body.forEach((row, rowIndex) => {
        const excelRow = this.currentSheet.getRow(this.currentRow);

        // Handle empty rows
        if (row.length === 0 || (row.length === 1 && !row[0])) {
          excelRow.height = 5;
          this.currentRow++;
          return;
        }

        let actualColIndex = 0;
        row.forEach((cell, colIndex) => {
          const excelCell = excelRow.getCell(actualColIndex + 1);
          const cellContent = this.extractCellContent(cell);

          excelCell.value = cellContent;

          // Apply default data style
          excelCell.style = { ...this.settings.styles.data };

          // Check for colSpan
          let colSpan = 1;
          if (cell && typeof cell === "object" && cell.colSpan) {
            colSpan = cell.colSpan;
            if (colSpan > 1) {
              this.currentSheet.mergeCells(
                this.currentRow,
                actualColIndex + 1,
                this.currentRow,
                actualColIndex + colSpan
              );
            }
          }

          // Apply cell-specific styles
          this.applyCellStyle(cell, excelCell, colIndex, columnStyles);

          // Apply didParseCell callback if provided
          if (didParseCell) {
            const mockData = {
              row: { index: rowIndex, section: "body" },
              column: { index: colIndex },
              cell: { styles: {} },
            };
            didParseCell(mockData);
            if (mockData.cell.styles.halign) {
              excelCell.alignment = {
                ...excelCell.alignment,
                horizontal: mockData.cell.styles.halign,
              };
            }
          }

          // Apply total line styling with underlines (similar to beautyAutoLine in PDF)

          const isLabelRow = rowIndex === 0;
          const isTotalRow = lineSettings.totalLines?.includes(rowIndex);
          const isSubTotalRow = lineSettings.subTotalLines?.includes(rowIndex);
          const isDoubleTotalRow =
            lineSettings.totalDoubleLines?.includes(rowIndex);
          const isDoubleSubTotalRow =
            lineSettings.subTotalDoubleLines?.includes(rowIndex);

          let shouldHaveLine = false;
          let shouldHaveDoubleLine = false;

          const checkSettings = (settings) => {
            return (
              settings && settings.length > 0 && settings.includes(colIndex)
            );
          };
          // Determine if this cell should have a line based on PDF logic
          if (isLabelRow && checkSettings(lineSettings.labelRows)) {
            // Label row: columns 1, 2 (the $ columns) get lines
            shouldHaveLine = true;
          } else if (isTotalRow && checkSettings(lineSettings.totalRows)) {
            // Total lines: column 2 gets line
            shouldHaveLine = true;
          } else if (
            isSubTotalRow &&
            checkSettings(lineSettings.subTotalRows)
          ) {
            // SubTotal lines: column 1 gets line
            shouldHaveLine = true;
          }

          if (
            isDoubleTotalRow &&
            checkSettings(
              lineSettings.totalDoubleRows || lineSettings.totalRows
            )
          ) {
            // Double total: column 2 gets double line
            shouldHaveDoubleLine = true;
            shouldHaveLine = false; // Double line takes precedence
          }

          if (isDoubleSubTotalRow && checkSettings(lineSettings.subTotalRows)) {
            // Double subtotal: column 2 gets double line
            shouldHaveDoubleLine = true;
            shouldHaveLine = false; // Double line takes precedence
          }

          // Apply bold and background for total/subtotal rows
          // if (isTotalRow || isDoubleTotalRow || isSubTotalRow) {
          //   excelCell.font = { ...excelCell.font, bold: true };
          //   excelCell.fill = {
          //     type: "pattern",
          //     pattern: "solid",
          //     fgColor: { argb: "F2F2F2" },
          //   };
          // }

          // Add underline border (similar to beautyAutoLine)
          if (shouldHaveLine || shouldHaveDoubleLine) {
            if (!excelCell.border) {
              excelCell.border = {};
            }

            if (shouldHaveDoubleLine) {
              // Double line - use thick border (Excel doesn't have true double, so use thick)
              excelCell.border.bottom = {
                style: "double",
                color: { argb: "000000" },
              };
            } else if (shouldHaveLine) {
              // Single line
              excelCell.border.bottom = {
                style: "thin",
                color: { argb: "000000" },
              };
            }
          }

          actualColIndex += colSpan;
        });

        excelRow.height = 18;
        this.currentRow++;
      });
    }

    // Auto-fit columns
    this.autoFitColumns(this.currentSheet);
  }

  /**
   * Build notes section similar to PDF's buildAutoNote
   */
  buildNotes(notes, reviewNotes, forView = true, data = [], company) {
    if (!this.currentSheet) return;

    // Process review notes with calculator notes
    const processedReviewNotes = buildCalculatorNotes(reviewNotes, data);

    // Add spacing before notes
    this.currentRow++;

    // Add Notes/Comments section
    if (notes) {
      const notesHeaderRow = this.currentSheet.getRow(this.currentRow);
      notesHeaderRow.getCell(1).value = "Notes/Comments";
      notesHeaderRow.getCell(1).style = this.settings.styles.notesHeader;
      this.currentSheet.mergeCells(this.currentRow, 1, this.currentRow, 6);
      notesHeaderRow.height = 20;
      this.currentRow++;

      // Split notes into lines and add each line
      const noteLines = notes.split("\n");
      noteLines.forEach((line) => {
        if (line.trim()) {
          const noteRow = this.currentSheet.getRow(this.currentRow);
          noteRow.getCell(1).value = line;
          noteRow.getCell(1).style = this.settings.styles.notes;
          this.currentSheet.mergeCells(this.currentRow, 1, this.currentRow, 6);
          noteRow.height = 15;
          this.currentRow++;
        }
      });

      // Add spacing
      this.currentRow++;
    }

    // Add Internal Review Notes section (only if forView is true)
    if (processedReviewNotes && forView) {
      if (notes) {
        // Add spacing between sections
        this.currentRow++;
      }

      const reviewNotesHeaderRow = this.currentSheet.getRow(this.currentRow);
      reviewNotesHeaderRow.getCell(1).value = "Internal Review Notes";
      reviewNotesHeaderRow.getCell(1).style =
        this.settings.styles.reviewNotesHeader;
      this.currentSheet.mergeCells(this.currentRow, 1, this.currentRow, 6);
      reviewNotesHeaderRow.height = 20;
      this.currentRow++;

      // Split review notes into lines and add each line
      const reviewNoteLines = processedReviewNotes.split("\n");
      reviewNoteLines.forEach((line) => {
        if (line.trim()) {
          const reviewNoteRow = this.currentSheet.getRow(this.currentRow);
          reviewNoteRow.getCell(1).value = line;
          reviewNoteRow.getCell(1).style = this.settings.styles.reviewNotes;
          this.currentSheet.mergeCells(this.currentRow, 1, this.currentRow, 6);
          reviewNoteRow.height = 15;
          this.currentRow++;
        }
      });
    }
  }

  /**
   * Auto-fit columns to content
   */
  autoFitColumns(worksheet) {
    worksheet.columns.forEach((col) => {
      let maxLength = 10;
      col.eachCell({ includeEmpty: true }, (cell) => {
        const cellValue = cell.value;
        if (cellValue) {
          const cellLength =
            typeof cellValue === "string"
              ? cellValue.length
              : cellValue.toString().length;
          maxLength = Math.max(maxLength, cellLength);
        }
      });
      col.width = Math.min(maxLength + 2, 50);
    });
  }

  /**
   * Get the current workbook
   */
  getWorkbook() {
    return this.workbook;
  }

  /**
   * Get the current worksheet
   */
  getCurrentSheet() {
    return this.currentSheet;
  }

  /**
   * Generate buffer for download
   */
  async generateBuffer() {
    return this.workbook.xlsx.writeBuffer();
  }

  /**
   * Create filename
   */
  createFilename(forView, companyName, scheduleName) {
    return `${forView ? "Draft_" : ""}${companyName || "Company"}_${
      scheduleName || "Schedule"
    }_${new Date().toISOString().split("T")[0]}.xlsx`;
  }

  /**
   * Download the Excel file
   */
  async download(
    forView = false,
    companyName = "Company",
    scheduleName = "Schedule"
  ) {
    const filename = this.createFilename(forView, companyName, scheduleName);
    return this.downloadAs(filename);
  }

  async downloadAs(filename) {
    const buffer = await this.generateBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename || "Schedule.xlsx";
    link.click();
    URL.revokeObjectURL(link.href);
  }
}
